'use client'

import { createStaticPixQr, PixKey, sendPixQrByEmail } from '@/lib/api/financial/digital-account-api'
import { useState } from 'react'
import { MdContentCopy, MdEmail, MdQrCode2 } from 'react-icons/md'
import { toast } from 'react-toastify'
import CurrencyInput from './currency-input'

export default function ReceivePixModal({
    pixKeys,
    onClose,
    onSuccess,
}: {
    pixKeys: PixKey[]
    onClose: () => void
    onSuccess: () => void
}) {
    const [addressKey, setAddressKey] = useState(pixKeys[0]?.key || '')
    const [description, setDescription] = useState('')
    const [amountCents, setAmountCents] = useState<number | null>(null)
    const [multi, setMulti] = useState(true)
    const [creating, setCreating] = useState(false)
    const [created, setCreated] = useState<any>(null)
    const [showEmailForm, setShowEmailForm] = useState(false)
    const [recipientEmail, setRecipientEmail] = useState('')
    const [sendingEmail, setSendingEmail] = useState(false)

    const handleCreate = async () => {
        if (!addressKey) return toast.warning('Selecione uma chave PIX')
        setCreating(true)
        try {
            const res = await createStaticPixQr({
                addressKey,
                description: description.trim() || undefined,
                value: amountCents ? amountCents / 100 : undefined,
                allowsMultiplePayments: multi,
            })
            if (res.success && res.result) {
                setCreated(res.result)
            } else {
                toast.error(res.error || 'Erro ao gerar QR')
            }
        } finally {
            setCreating(false)
        }
    }

    const copyPayload = () => {
        if (!created?.payload) return
        navigator.clipboard.writeText(created.payload)
        toast.success('Código copiado')
    }

    const handleSendEmail = async () => {
        if (!recipientEmail.trim() || !/^.+@.+\..+$/.test(recipientEmail)) {
            return toast.warning('Informe um email valido')
        }
        if (!created?.encodedImage || !created?.payload) return
        setSendingEmail(true)
        try {
            const res = await sendPixQrByEmail({
                email: recipientEmail.trim(),
                encodedImage: created.encodedImage,
                payload: created.payload,
                valueCents: amountCents,
                description: description.trim() || null,
            })
            if (res.success) {
                toast.success(`QR enviado para ${recipientEmail.trim()}`)
                setShowEmailForm(false)
                setRecipientEmail('')
            } else {
                toast.error(res.error || 'Erro ao enviar email')
            }
        } finally {
            setSendingEmail(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-md">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
                    <MdQrCode2 className="text-blue-500" /> Receber via PIX
                </h3>

                {created ? (
                    <div className="space-y-3">
                        {created.encodedImage && (
                            <img src={`data:image/png;base64,${created.encodedImage}`} alt="QR Code" className="mx-auto w-48 h-48" />
                        )}
                        <div>
                            <p className="text-xs text-base-content/60 mb-1">PIX copia e cola</p>
                            <div className="flex gap-2">
                                <input className="input input-bordered input-xs flex-1 font-mono" readOnly value={created.payload || ''} />
                                <button className="btn btn-xs btn-secondary" onClick={copyPayload}><MdContentCopy /></button>
                            </div>
                        </div>

                        {showEmailForm ? (
                            <div className="flex flex-col gap-2 p-3 bg-base-200 rounded-lg">
                                <span className="text-sm font-medium text-base-content/80">Enviar QR por email</span>
                                <input
                                    type="email"
                                    className="input input-bordered input-sm"
                                    placeholder="destinatario@exemplo.com"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    disabled={sendingEmail}
                                    autoFocus
                                />
                                <div className="flex gap-2">
                                    <button
                                        className="btn btn-ghost btn-sm flex-1"
                                        onClick={() => { setShowEmailForm(false); setRecipientEmail('') }}
                                        disabled={sendingEmail}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="btn btn-secondary btn-sm flex-1"
                                        onClick={handleSendEmail}
                                        disabled={sendingEmail || !recipientEmail.trim()}
                                    >
                                        {sendingEmail ? <span className="loading loading-spinner loading-xs" /> : 'Enviar'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                className="btn btn-sm btn-outline w-full gap-2"
                                onClick={() => setShowEmailForm(true)}
                            >
                                <MdEmail /> Enviar por email
                            </button>
                        )}

                        <button className="btn btn-sm btn-secondary w-full" onClick={onSuccess}>Concluir</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {pixKeys.length === 0 ? (
                            <div className="alert alert-warning text-sm">
                                Cadastre uma chave PIX antes de gerar QR Code.
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-base-content/80">Chave PIX</span>
                                <select className="select select-bordered select-sm" value={addressKey} onChange={e => setAddressKey(e.target.value)}>
                                    {pixKeys.map(k => <option key={k.id} value={k.key}>{k.type}: {k.key}</option>)}
                                </select>
                            </div>
                        )}

                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Descrição</span>
                            <input className="input input-bordered input-sm" value={description} onChange={e => setDescription(e.target.value)} />
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Valor (opcional)</span>
                            <CurrencyInput valueCents={amountCents} onChange={setAmountCents} disabled={creating} />
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input type="checkbox" className="checkbox checkbox-sm" checked={multi} onChange={e => setMulti(e.target.checked)} />
                            <span className="text-sm">Permite múltiplos pagamentos</span>
                        </label>

                        <div className="modal-action">
                            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
                            <button className="btn btn-secondary btn-sm" onClick={handleCreate} disabled={creating || pixKeys.length === 0}>
                                {creating ? <span className="loading loading-spinner loading-xs" /> : 'Gerar QR'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
